import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { imgUrl, productImgUrl } from '../utils/imgUrl';
import { useSale, salePrice, saleAppliesTo, salePercent } from '../utils/sale';
import { useSyncedTick } from '../utils/syncedSlideshow';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const sale = useSale();
  const onSale = saleAppliesTo(sale, product);
  const sp = salePrice(product.price, sale, product);

  // Slides: images only (videos play on the detail page). Cap at 3 for the listing.
  const imgList = (product.images && product.images.length > 0)
    ? product.images
    : (product.image ? [product.image] : []);
  const slides = imgList.slice(0, 3);
  const hasMultipleSlides = slides.length > 1;

  const tick = useSyncedTick();
  const currentIndex = hasMultipleSlides ? tick % slides.length : 0;

  // Preload every slide once the card mounts so opacity cross-fades are instant
  // and never reveal a blank/white frame mid-transition.
  useEffect(() => {
    slides.forEach((src) => {
      const pre = new Image();
      pre.src = productImgUrl(product, src, 600);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  const handleAddToCart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    addToCart(product, 1);
  };

  const handleCardClick = () => {
    const slug = product.slug || product.id;
    navigate(`/products/${product.category}/${slug}`);
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      onClick={handleCardClick}
      className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer"
    >
      <div className="relative overflow-hidden aspect-square bg-gray-100">
        {onSale && (
          <span className="absolute top-3 left-3 z-10 bg-cyan-600 text-white text-xs font-bold rounded-full px-2.5 py-1 shadow" data-testid={`sale-badge-${product.id}`}>
            {salePercent(sale, product)}% OFF
          </span>
        )}
        {slides.length > 0 ? (
          // Stack all slides and cross-fade with opacity. Because every layer is
          // already in the DOM (and preloaded), switching opacity never shows a
          // white gap between pictures.
          slides.map((src, i) => (
            <img
              key={src}
              src={productImgUrl(product, src, 600)}
              alt={`${product.name} in-pool furniture - ${product.category}`}
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
              onError={(e) => {
                const fallback = productImgUrl(product, product.image, 600);
                if (product.image && e.target.src !== fallback) {
                  e.target.src = fallback;
                }
              }}
              className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ease-in-out ${
                i === currentIndex ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ))
        ) : (
          <img
            src={productImgUrl(product, product.image, 600)}
            alt={`${product.name} - Tenjam SPLASH Series in-pool ${product.category}`}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110"
          />
        )}

        {/* Slide Indicators */}
        {hasMultipleSlides && (
          <div className="absolute top-3 right-3 z-10 flex space-x-1">
            {slides.map((_, index) => (
              <div
                key={index}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'w-5 bg-cyan-400' : 'bg-white/60'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="p-6">
        {/* Category Badge */}
        <div className="mb-3">
          <span className="text-xs font-medium text-cyan-600 bg-cyan-50 px-3 py-1 rounded-full">
            {product.category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
          </span>
        </div>

        {/* Product Name */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors duration-300 line-clamp-2">
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center space-x-2 mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={14}
                className={i < Math.floor(product.rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
              />
            ))}
          </div>
          <span className="text-sm text-gray-600">
            {product.rating} ({product.reviews})
          </span>
        </div>

        {/* Features */}
        <div className="mb-4">
          <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
        </div>

        {/* Price & Cart */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div>
            {onSale ? (
              <div className="flex flex-col leading-tight">
                <span className="text-xs text-gray-400 line-through">
                  ${product.price.toLocaleString()}
                </span>
                <span className="text-2xl font-bold text-cyan-600">
                  ${sp.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            ) : (
              <span className="text-2xl font-bold text-gray-900">
                ${product.price.toLocaleString()}
              </span>
            )}
          </div>
          <button
            onClick={handleAddToCart}
            className="p-2 bg-cyan-100 text-cyan-600 rounded-lg hover:bg-cyan-200 transition-all duration-300"
            title="Add to Cart"
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
